import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Activity,
  KeyRound,
  LogOut,
  Shield,
  CreditCard,
  Package,
  Tag,
  Receipt,
  Clock,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/tenants', label: 'Tenants', icon: Users, end: false },
  { to: '/admin/health', label: 'System Health', icon: Activity, end: false },
  { to: '/admin/activation', label: 'Activation', icon: KeyRound, end: false },
];

const billingNavItems = [
  { to: '/admin/billing', label: 'Billing Overview', icon: CreditCard, end: true },
  { to: '/admin/billing/products', label: 'Products', icon: Package, end: false },
  { to: '/admin/billing/pricing', label: 'Pricing', icon: Tag, end: false },
  { to: '/admin/billing/subscriptions', label: 'Subscriptions', icon: Receipt, end: false },
  { to: '/admin/billing/trials', label: 'Trials', icon: Clock, end: false },
];

function NavSection({
  title,
  items,
}: {
  title?: string;
  items: { to: string; label: string; icon: React.ElementType; end: boolean }[];
}) {
  return (
    <div>
      {title && (
        <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
          {title}
        </p>
      )}
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-indigo-50 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
            )
          }
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {label}
        </NavLink>
      ))}
    </div>
  );
}

export function AdminLayout() {
  const navigate = useNavigate();

  function handleLogout() {
    localStorage.removeItem('adminToken');
    void navigate('/admin/login');
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="flex h-screen w-60 flex-col border-r border-gray-200 bg-white">
        <div className="flex h-14 items-center gap-2 px-4 font-semibold text-lg border-b border-gray-100">
          <Shield className="h-5 w-5 text-indigo-600" aria-hidden="true" />
          Grid2 Admin
        </div>

        <nav className="flex-1 space-y-6 overflow-y-auto px-2 py-4" aria-label="Admin navigation">
          <NavSection items={navItems} />
          <NavSection title="Billing" items={billingNavItems} />
        </nav>

        <div className="border-t border-gray-100 p-2">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center border-b border-gray-200 bg-white px-6">
          <h1 className="text-sm font-medium text-gray-500">
            Grid2 Admin Console
          </h1>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
